import { SafePaddingContainer } from "@/components/SafePaddingContainer";
import { Header } from "@/components/Index/Header";
import { PromoCard } from "@/components/Index/PromoCard";
import { Badge } from "@/components/ui/badge";
import { BlinkingIndicator } from "@/components/BlinkingIndicator";
import { TrendingCard } from "@/components/TrendingCard";
import { OtherAvailableCards } from "@/components/Index/OtherAvailableCards";
import { BalanceCard } from "@/components/BalanceCard";
import { SkillScratchCard } from "@/components/Index/SkillScratchCard";
import { Footer } from "@/components/Index/Footer";
import { Button } from "@/components/ui/button";
import { UnplayedCards } from "@/components/Index/UnplayedCards";
import { useOutletContext } from "react-router-dom";
import type { HeaderProps } from "@/components/Index/Header";
import {
  show as showIntercomOverlay,
} from "@intercom/messenger-js-sdk";
import { useSwipeDetection } from "@/hooks/useSwipeDetection";
import { JackpotCard } from "@/components/Index/JackpotCard";
import { useFirebaseFunction } from "@/hooks/useFirebaseFunction";
import { useEffect } from "react";
import type { FirebaseTimestamp } from "@skillscratch/shared-types";
import { useModal } from "@/hooks/useModal";
import { NotificationModal } from "@/components/Modals/NotificationModal";
import { FreeCardModal } from "@/components/Modals/FreeCardModal";
import { useGamePacks } from '@/hooks/useGamePacks';
import { hasTimePassed } from "@/helpers/hasTimePassed";
import { addHoursToTimestamp } from "@/helpers/hasTimePassed";
import type { SerializedFirebaseTimestamp } from "@/components/PromoTimer";

// import { requestNotificationAuthorization } from "@/bridge/requestNotificationAuthorization";
// import { useEffect } from "react";
// import { useNavigate } from "react-router-dom";

// Define types for our API calls
type GetUserPrivateInfoRequest = {
    fields?: string[];
};
  
type GetUserPrivateInfoResponse = {
    userInfo: {
      pushNotificationsEnabled?: boolean;
      lastAskedForPushNotifications?: FirebaseTimestamp; // When we last asked for push notifications
      lifetimeEarnings?: number;
      purchaseMade?: boolean;
      lastFreeCardPromo: SerializedFirebaseTimestamp;
    };
};


export function IndexPage() {
  // const navigate = useNavigate();
  const { sidebarOpen, setSidebarOpen } = useOutletContext<HeaderProps>();
  const { openModal } = useModal();
  const [userGamePacks, isUserGamePacksLoading, userGamePacksError] = useGamePacks();


  // Use hook to fetch notification settings
  const [userPrivateData, userPrivateDataLoading] = useFirebaseFunction<
    GetUserPrivateInfoRequest, 
    GetUserPrivateInfoResponse
  >(
    'getUserPrivateInfo',
    (fn) => fn({ fields: [
        "lastAskedForPushNotifications", 
        "pushNotificationsEnabled", 
        "lifetimeEarnings", 
        "purchaseMade", 
        "lastFreeCardPromo"
      ] 
    }),
    []
  );


  useSwipeDetection({
    minSwipeDistance: 50,
    onSwipeLeft: () => {
      if (sidebarOpen) {
        setSidebarOpen(false);
        return;
      }

      // navigate("/about-us", {
      //   replace: false,
      //   viewTransition: true,
      // });
    },
    onSwipeRight: () => {
      setSidebarOpen(true);
    }
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (userPrivateDataLoading || !userPrivateData?.userInfo) {
      return;
    }

    const { lastAskedForPushNotifications } = userPrivateData.userInfo;

    if (!lastAskedForPushNotifications) {
      openModal(<NotificationModal />);
    }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userPrivateData, userPrivateDataLoading])

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    console.log('[test]');
    if (userPrivateDataLoading || !userPrivateData?.userInfo || isUserGamePacksLoading) {
      return;
    }

    if (!userGamePacks || userGamePacks.length > 0) {
      return;
    }

    const { lifetimeEarnings, purchaseMade, lastFreeCardPromo } = userPrivateData.userInfo;

    const oneDayPassed = !lastFreeCardPromo || hasTimePassed(addHoursToTimestamp(lastFreeCardPromo, 24));
    const hasntWonMoneyBefore = lifetimeEarnings != undefined && lifetimeEarnings == 0;

    if (hasntWonMoneyBefore && purchaseMade && oneDayPassed) {
      openModal(<FreeCardModal />);
    }


  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userPrivateData?.userInfo?.purchaseMade, userPrivateData?.userInfo?.lifetimeEarnings, 
    userPrivateData?.userInfo?.lastFreeCardPromo,
    userPrivateDataLoading, userGamePacks, isUserGamePacksLoading])


  // useEffect(() => {
  //   (async () => {
  //     await requestNotificationAuthorization();  
  //   })();
  // }, []);


  return (
    <div className="flex flex-col h-screen overflow-y-scroll">
      {/* Header section with header orientation */}
      <SafePaddingContainer orientation="header" className="bg-blue fixed z-50 pb-2 -translate-y-1">
        <Header sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen}/>
      </SafePaddingContainer>

      <SafePaddingContainer orientation="header" className="bg-blue pb-10">
        <div className='opacity-0'>
          <Header sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen}/>
        </div>
      </SafePaddingContainer>
      
      <SafePaddingContainer className="bg-blue">
        <div className='mb-10'>
          <PromoCard />
        </div>
        <div className="flex justify-center w-full relative btn-bounce-subtle">
            {/* <div className=""> */}
                <Badge className="absolute -translate-y-5 px-3 py-1 tracking-tight word-spacing-tighter text-base text-xl text-white rounded-lg"> 
                    <BlinkingIndicator/>
                    Win up to $4,600,000
                </Badge>
            {/* </ div> */}
        </div>
      </SafePaddingContainer>
      

      <SafePaddingContainer orientation="neither" className="bg-blue-dark flex-grow flex flex-col space-y-5 pb-16">
        <div className='mt-14 btn-bounce-subtle'>
            <TrendingCard />
        </div>
        
        <UnplayedCards userGamePacks={userGamePacks} isUserGamePacksLoading={isUserGamePacksLoading} userGamePacksError={userGamePacksError} />
        <OtherAvailableCards />

        <BalanceCard />
        <JackpotCard />

        <SkillScratchCard />

        <div className="flex justify-center w-full relative btn-bounce-subtle">
          <Badge className="absolute translate-y-6 px-4 py-4 tracking-tight word-spacing-tighter text-base 
          text-xl text-slate rounded-lg w-full flex justify-between bg-white max-w-md"> 
              <span className='font-normal'>Need support? Say hi! </span>
              <Button variant='wallet-btn' size='walletBtn' className="bg-blue-dark text-white"
                shine
                onClick={() => showIntercomOverlay()}>
                Get help
              </Button>
          </Badge>
        </div>
      </SafePaddingContainer>

      {/* Main content and footer with footer orientation */}
      <SafePaddingContainer orientation="footer" className="bg-blue">
        <div className="mt-16 mb-24">
          <Footer />
        </div>
      </SafePaddingContainer>
    </div>
  );
}